import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { getArticleSchema, getBreadcrumbSchema, getFAQPageSchema } from '@/components/feature/SchemaOrg';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import type { ArticleSection } from '@/mocks/articles';

function ArticleContent({ section }: { section: ArticleSection }) {
  if (section.type === 'heading') {
    const Tag = `h${section.level || 2}` as keyof JSX.IntrinsicElements;
    return <Tag className={`article-heading ${section.level === 2 ? 'text-2xl font-bold mt-8 mb-4 text-foreground-950' : 'text-xl font-semibold mt-6 mb-3 text-foreground-900'}`}>{section.content}</Tag>;
  }
  if (section.type === 'paragraph') {
    return <p className="mb-4 leading-relaxed text-foreground-700">{section.content}</p>;
  }
  if (section.type === 'list') {
    return (
      <ul className="mb-4 space-y-2 pl-5">
        {section.items?.map((item, i) => (
          <li key={i} className="text-foreground-700 leading-relaxed flex items-start gap-2">
            <span className="text-accent-500 mt-1.5 flex-shrink-0">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 whitespace-nowrap">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article.relatedArticles);
  const pageUrl = `https://www.nanke-health.com/article/${article.slug}`;

  return (
    <>
      <SEOHead
        title={`${article.title} - 男性健康科普`}
        description={article.excerpt}
        keywords={`龟头责,${article.category},龟头责${article.category},男性生殖器炎症`}
        canonicalUrl={pageUrl}
        ogType="article"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getBreadcrumbSchema([
        { name: '首页', url: 'https://www.nanke-health.com' },
        { name: article.category, url: `https://www.nanke-health.com/category/${article.categorySlug}` },
        { name: article.title, url: pageUrl },
      ])) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getArticleSchema({
        title: article.title,
        description: article.excerpt,
        publishDate: article.publishDate,
        url: pageUrl,
      })) }} />
      {article.faqs.length > 0 && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getFAQPageSchema(article.faqs)) }} />
      )}

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Article Header */}
        <div className="pt-24 pb-8 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <Breadcrumb items={[
              { name: '首页', href: '/' },
              { name: article.category, href: `/category/${article.categorySlug}` },
              { name: article.title },
            ]} />
            <div className="mt-6">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-4 whitespace-nowrap">
                {article.category}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-foreground-600 mb-4 leading-relaxed">{article.excerpt}</p>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读约{article.readTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  男性健康科普中心
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <article className="w-full px-4 md:px-6 max-w-4xl mx-auto py-10">
          <div className="article-content">
            {article.content.map((section, index) => (
              <ArticleContent key={index} section={section} />
            ))}
          </div>

          {/* Disclaimer */}
          <div className="mt-10 p-5 bg-background-100 border border-background-200/70 rounded-lg">
            <p className="text-xs text-foreground-500 leading-relaxed">
              <strong className="text-foreground-700">免责声明：</strong>
              本文内容仅供科普参考，不构成医疗诊断或治疗建议。如果您有相关症状，请及时到正规医院泌尿外科或男科就诊，在专业医生指导下进行诊断和治疗。切勿自行诊断或用药。
            </p>
          </div>
        </article>

        {/* FAQs */}
        {article.faqs.length > 0 && (
          <section className="bg-background-100 py-10">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                常见问题
              </h2>
              <div className="space-y-3">
                {article.faqs.map((faq, index) => (
                  <details key={index} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                      <span className="font-heading text-sm font-medium text-foreground-900 pr-4">
                        {faq.question}
                      </span>
                      <i className="ri-add-line text-foreground-500 group-open:hidden flex-shrink-0 text-lg"></i>
                      <i className="ri-subtract-line text-foreground-500 hidden group-open:block flex-shrink-0 text-lg"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group block bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-200/60 transition-all duration-300"
                  >
                    <span className="inline-block px-2 py-0.5 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-3 whitespace-nowrap">
                      {ra.category}
                    </span>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 mb-2">
                      {ra.title}
                    </h3>
                    <p className="text-sm text-foreground-600 line-clamp-2 mb-3">{ra.excerpt}</p>
                    <div className="text-xs text-foreground-500 flex items-center gap-1">
                      <i className="ri-arrow-right-line"></i>
                      阅读全文
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}